// Client-side validation for the Google Cloud GRC connector form (Settings → Integrations).
// Mirrors the backend GcpConnectorValidation rules so the user sees the same message before the
// request is sent. The backend stays the enforcement boundary: it also rejects duplicate top-level
// keys, which JSON.parse cannot detect. Error strings are fixed sentences and never echo the input.

export const GCP_MAX_KEY_LENGTH = 16384;
export const GCP_TOKEN_URI = "https://oauth2.googleapis.com/token";

const ERR_KEY_REQUIRED = "A Google Cloud service account key is required.";
const ERR_KEY_TOO_LARGE = "The service account key is too large. Paste the JSON key file exactly as downloaded from Google Cloud.";
const ERR_KEY_NOT_JSON = "The service account key is not valid JSON. Paste the JSON key file exactly as downloaded from Google Cloud.";
const ERR_KEY_TYPE = "The key must be a Google Cloud service account key (type \"service_account\").";
const ERR_KEY_INCOMPLETE = "The service account key is missing client_email, private_key or project_id.";
const ERR_KEY_TOKEN_URI = "The service account key's token_uri must be the standard Google token endpoint (https://oauth2.googleapis.com/token).";

const ERR_SCOPE_TYPE = "Scan scope must be ORGANIZATION or PROJECT.";
const ERR_SCOPE_ORG_ID = "The organization ID must be the numeric Google Cloud organization ID (up to 20 digits).";
const ERR_SCOPE_PROJECT = "The project ID must be 6–30 characters: lowercase letters, digits and hyphens, starting with a letter and not ending with a hyphen.";
const ERR_DISPLAY_NAME = "A display name is required.";

const ORG_ID_RE = /^\d{1,20}$/;
const PROJECT_ID_RE = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;

const isBlank = (v) => typeof v !== "string" || v.trim() === "";
const readString = (obj, name) => (typeof obj[name] === "string" ? obj[name] : null);

/**
 * Validates a service-account key JSON string.
 * @returns {{ ok: boolean, error: string|null, clientEmail: string|null }}
 */
export function validateGcpKeyJson(text) {
  const fail = (error) => ({ ok: false, error, clientEmail: null });
  if (isBlank(text)) return fail(ERR_KEY_REQUIRED);
  if (text.length > GCP_MAX_KEY_LENGTH) return fail(ERR_KEY_TOO_LARGE);

  let root;
  try { root = JSON.parse(text); } catch { return fail(ERR_KEY_NOT_JSON); }
  if (root === null || typeof root !== "object" || Array.isArray(root)) return fail(ERR_KEY_NOT_JSON);

  if (readString(root, "type") !== "service_account") return fail(ERR_KEY_TYPE);

  const clientEmail = readString(root, "client_email");
  if (isBlank(clientEmail) || isBlank(readString(root, "private_key")) || isBlank(readString(root, "project_id")))
    return fail(ERR_KEY_INCOMPLETE);

  if (Object.prototype.hasOwnProperty.call(root, "token_uri") && root.token_uri !== GCP_TOKEN_URI)
    return fail(ERR_KEY_TOKEN_URI);

  return { ok: true, error: null, clientEmail: clientEmail.trim() };
}

/**
 * Validates the scan scope. The type is compared upper-case and the id is trimmed, like the backend.
 * @returns {{ ok: boolean, error: string|null }}
 */
export function validateGcpScope(scopeType, scopeId) {
  const type = (scopeType || "").trim().toUpperCase();
  const id = (scopeId || "").trim();
  if (type === "ORGANIZATION") return ORG_ID_RE.test(id) ? { ok: true, error: null } : { ok: false, error: ERR_SCOPE_ORG_ID };
  if (type === "PROJECT") return PROJECT_ID_RE.test(id) ? { ok: true, error: null } : { ok: false, error: ERR_SCOPE_PROJECT };
  return { ok: false, error: ERR_SCOPE_TYPE };
}

/**
 * Decides which validators a save runs and returns the first error, or null when the form is valid.
 * The key is required on create. On edit a blank (or whitespace-only) key keeps the stored key, so
 * it is validated only when something was entered. The key is checked before the scope, like the backend.
 * @param {{ displayName?: string, gcpScopeType?: string, gcpScopeId?: string, gcpServiceAccountKeyJson?: string }} form
 * @param {boolean} isEdit
 * @returns {string|null}
 */
export function gcpSaveValidationErrors(form, isEdit) {
  const f = form || {};
  if (isBlank(f.displayName)) return ERR_DISPLAY_NAME;
  if (!isEdit || !isBlank(f.gcpServiceAccountKeyJson)) {
    const key = validateGcpKeyJson(f.gcpServiceAccountKeyJson);
    if (!key.ok) return key.error;
  }
  const scope = validateGcpScope(f.gcpScopeType, f.gcpScopeId);
  return scope.ok ? null : scope.error;
}
